import { Features } from '@alsid/common'
import { emptyServerProperties } from '@app/_init/createApp/initializeTul/emptyResponses'
import { AppRouteName } from '@app/routes'
import type { StoreRoot } from '@app/stores'
import { isDefined } from '@libs/isDefined'
import { getUserTelemetryId } from '@libs/telemetry/utils'
import type {
  ITulConfig,
  ServerProperties
} from '@tenable/tenable-universal-layout'
import { initTul } from '@tenable/tenable-universal-layout'

export async function initializeTul(storeRoot: StoreRoot) {
  const { storePendo } = storeRoot.stores
  const { logger } = storeRoot

  const featureWorkspaceAppswitcherInTad =
    storeRoot.stores.storeRbac.isUserGrantedAccordingFeatureFlag(
      Features.WORKSPACE_APPSWITCHER_IN_TAD
    )

  const canAccessTenableOne =
    storeRoot.stores.storeAbout.productAssociation?.isAssociatedToTenableOne()
  /**
   * Retrieve server properties from Kapteyn server middleware
   * (which queries Eridanis directly).
   */
  const serverProperties =
    (await storeRoot.stores.storeAuthentication.fetchServerProperties({
      silentCatch: true
    })) ?? null

  const cloudStatistics =
    (await storeRoot.stores.storeAuthentication.fetchCloudStatistics({
      silentCatch: true
    })) ?? null

  const tenableSession =
    featureWorkspaceAppswitcherInTad || canAccessTenableOne
      ? await storeRoot.stores.storeAuthentication.fetchSession({
          silentCatch: true
        })
      : null

  const pendoParameters = cloudStatistics?.getPendoParameters()

  const tadLicenseOnlyServerProperties: ServerProperties = {
    ...emptyServerProperties,
    analytics: {
      siteId: cloudStatistics?.siteId ?? '',
      enabled: true,
      key: ''
    }
  }

  const telemetryServerUrlIsDefined =
    isDefined(storeRoot.environment.config.app.telemetry.serverurl) &&
    storeRoot.environment.config.app.telemetry.serverurl.length > 0

  const tulConfig: ITulConfig = {
    as: 'saas',
    featureFlags: {
      featureFlagsDataGetter: async featureFlags => {
        let flags: Record<string, string> = {}
        try {
          flags = (await fetch('/w/tenable-services-proxy/feature-flags', {
            body: JSON.stringify(featureFlags),
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            }
          }).then(v => v.json())) as Record<string, string>
        } catch (err) {
          logger.error('Unable to get the feature flags', err)
        }
        return flags
      }
    },
    i18n: {
      language: 'en' // TODO: update this part to take care of the user session language
    },
    services: {
      session: {
        enabled: true,

        sessionDataGetter: () => {
          return Promise.resolve(
            tenableSession || storeRoot.stores.storeAuthentication.tulSession
          )
        },

        // avoid heartbeat check of the Tenable session as we don't rely on a irontoken based session
        checkSessionTimeout: false,

        parameters: {
          // don't use Irontoken, only used by apps living on the Tenable workspace,
          // which is not the case of TIE
          disableIronToken: true
        },
        renderComponents: {
          accountPanel: () =>
            `<div slot="user-action"><slot name="custom-user-action"></slot></div>`
        }
      },

      serverProperties: {
        enabled: true,

        /**  if serverProperties from Tenable endpoint are available use them
         *  if they are not available, and if user is tad license, use a constructed serverProperties to instantiate TUL telemetry
         * */
        serverPropertiesDataGetter: () => {
          return Promise.resolve(
            serverProperties ||
              (storeRoot.stores.storeAbout.productAssociation?.isAssociatedToTenableAd() &&
                tadLicenseOnlyServerProperties) ||
              emptyServerProperties
          )
        },

        renderComponents: {
          // if no serverProperties, don't render appsPanel
          appsPanel: isDefined(serverProperties)
        },

        parameters: {
          workspaceDomain: storeRoot.environment.config.tul?.workspacedomain
        }
      },

      settings: {
        enabled: true,
        renderComponents: {
          settingsPanel: () =>
            `<div slot="settings-action"><slot name="custom-settings-action"></slot></div>`
        }
      },

      alerts: {
        enabled: true,
        renderComponents: {
          alertsPanel: () =>
            `<div slot="notifications-action"><slot name="custom-notifications-action"></slot></div>`
        }
      },

      telemetry: {
        enabled: cloudStatistics?.isEnabled ?? false,
        telemetryPropertiesDataGetter: () =>
          Promise.resolve(
            tenableSession || {
              containerName: cloudStatistics?.customerName ?? '',
              containerUuid:
                cloudStatistics?.containerUuid?.toLowerCase() ?? '',
              uuid: getUserTelemetryId(storeRoot, cloudStatistics) ?? ''
            }
          ),
        parameters: {
          namespace: 'tenable-io/production',
          development: isDefined(serverProperties?.analytics.siteId)
            ? serverProperties.analytics.siteId.startsWith('qa-')
            : isDefined(cloudStatistics)
              ? cloudStatistics.environmentType !== 'PROD'
              : true,
          debug: false,

          autotrack: {
            attribution: true,
            // autocapture is not tracking iframes pageviews. They will be sent manually.
            pageViews: {
              trackOn: () => {
                const path = window.location.pathname
                // Tracking will be delegated to iframes
                const excludedRoutes = [
                  storeRoot.appRouter.getRoutePathname(
                    AppRouteName.ExposureOverview
                  ),
                  storeRoot.appRouter.getRoutePathname(
                    AppRouteName.Inventories
                  ),
                  storeRoot.appRouter.getRoutePathname(AppRouteName.Identities),
                  storeRoot.appRouter.getRoutePathname(
                    AppRouteName.ExposureInstances
                  ),
                  storeRoot.appRouter.getRoutePathname(
                    AppRouteName.ExposureCenter
                  )
                ]

                return !excludedRoutes.includes(path)
              }
            },
            sessions: true,
            formInteractions: true,
            fileDownloads: true,
            elementInteractions: true
          },

          serverUrl: telemetryServerUrlIsDefined
            ? storeRoot.environment.config.app.telemetry.serverurl
            : undefined
        }
      },

      chats: {
        enabled: false
      },

      pendo: {
        enabled: storePendo.isPendoEnabled,

        pendoDataGetter: () => {
          const tulPendoData = pendoParameters?.tulPendoData

          if (!tulPendoData) {
            throw new Error(
              'Cant initialize Pendo, check CloudStatistics parameters.'
            )
          }

          return Promise.resolve(tulPendoData)
        },
        initOptions: {
          autoFrameInstall: true,
          preferBroadcastChannel: true,
          frameIdentitySync: true
        },

        parameters: {
          apikey:
            storeRoot.environment.config.tul?.services?.pendo?.parameters
              ?.apikey ?? ''
        },

        metadata: {
          visitor: [
            {
              name: 'adUiLanguage',
              value: storeRoot.appTranslator.language
            },
            {
              name: 'username',
              value:
                storeRoot.stores.storeAuthentication.cloudStatistics
                  ?.userUuid ?? ''
            },
            {
              name: 'email',
              value: storeRoot.stores.storeAuthentication.whoAmI?.email ?? ''
            }
          ],
          account: [
            {
              name: 'adSite',
              value:
                storeRoot.stores.storeAuthentication.cloudStatistics?.siteId ??
                ''
            },
            {
              name: 'adEnvironmentType',
              value:
                storeRoot.stores.storeAuthentication.cloudStatistics
                  ?.environmentType ?? ''
            },
            {
              name: 'adCustomerId',
              value:
                storeRoot.stores.storeAuthentication.cloudStatistics
                  ?.customerName ?? ''
            }
          ]
        }
      },

      streamio: {
        enabled: false
      },

      appSpecific: {
        enabled: true,
        appSpecificHTML: () => `
            <div slot="app-specific-actions">
              <slot name="custom-app-specific-actions"></slot>
            </div>
            ${
              !storePendo.isPendoEnabled
                ? `
                  <div slot="help-action">
                    <slot name="custom-help-action" /></slot>
                  </div>
                `
                : ''
            }
          `
      },

      beamer: {
        enabled: false
      }
    },

    app: {
      id: 'ad',
      production: storeRoot.environment.config.production,

      // useDefaultCSS: false,

      logo: {
        productName: 'Identity Exposure',
        source: '/w/assets/images/logos'
      },

      sidebar: {
        hamburger: true
      },

      handlers: {
        onTulNavigate: ({ url }) => {
          // Handle redirection of the AppsSwitcher
          document.location.href = url
        },

        /**
         * Set the expanded flag when clicking on a TUL's hamburger icon.
         */
        onTulHamburgerClick: ({ tulEvent }) => {
          const { storeMenuMainNavigation } = storeRoot.stores.storeLayout
          storeMenuMainNavigation.setExpanded(tulEvent.detail.navigationOpened)
        }
      }
    }
  }

  return initTul(null, tulConfig)
}
